/**
 * Exit Intent Popup Component
 * Captures users who are about to leave the page without completing signup/onboarding
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Gift, Mail, ArrowRight, X, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExitIntentPopup = ({ pageName = 'signup' }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if already shown this session
    const alreadyShown = sessionStorage.getItem('exit_intent_shown');
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    // Desktop: Mouse leave detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown && !showPopup) {
        setShowPopup(true);
        setHasShown(true);
        sessionStorage.setItem('exit_intent_shown', 'true');
      }
    };

    // Mobile: Back button / page visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !hasShown) {
        // User is leaving - we can't show popup but can track
        trackExitIntent();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasShown, showPopup]);

  const trackExitIntent = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/analytics/exit-intent`, {
        page: pageName,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to track exit intent:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/exit-intent/capture`, {
        email,
        page: pageName,
        offer: 'SAVE20',
        timestamp: new Date().toISOString()
      });
      
      setSubmitted(true);
      toast.success('Check your email for the special offer!');
      
      // Close after 3 seconds
      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!showPopup) return null;

  return (
    <Dialog open={showPopup} onOpenChange={setShowPopup}>
      <DialogContent className="sm:max-w-md">
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-4">
                <div className="bg-emerald-100 p-4 rounded-full">
                  <Gift className="h-8 w-8 text-emerald-600" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl">
                Wait! Don't Leave Yet!
              </DialogTitle>
              <DialogDescription className="text-center">
                We noticed you're about to leave. Here's a special offer just for you!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white rounded-lg p-4 text-center">
                <Badge className="bg-white text-emerald-600 mb-2">LIMITED TIME</Badge>
                <h3 className="text-2xl font-bold">20% OFF</h3>
                <p className="text-sm opacity-90">Your First 3 Months</p>
                <p className="text-xs mt-2">Use code: <span className="font-mono font-bold">SAVE20</span></p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Enter your email for the offer"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Get My 20% Discount'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
              
              <p className="text-xs text-center text-gray-500">
                By submitting, you agree to receive emails from Estate Flow.
                We'll never spam you.
              </p>
              
              <button
                onClick={() => setShowPopup(false)}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                No thanks, I'll pay full price
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="bg-emerald-100 p-4 rounded-full inline-flex mb-4">
              <Mail className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Check Your Inbox!</h3>
            <p className="text-gray-600">
              We've sent your 20% discount code to <strong>{email}</strong>
            </p>
            <Badge className="mt-4 bg-emerald-100 text-emerald-700">
              Code: SAVE20
            </Badge>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
